import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { from } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import {
    JeTierNachtragenDialogInput,
    JeTierNachtragenDialogResult,
} from './gruppe-tier-je-tier-nachtragen-dialog';

@Component({
    selector: 'kiene-gruppe-tier-je-tier-nachtragen-dialog',
    templateUrl: './gruppe-tier-je-tier-nachtragen-dialog.component.html',
    styleUrls: ['./gruppe-tier-je-tier-nachtragen-dialog.component.scss'],
})
export class GruppeTierJeTierNachtragenDialogComponent implements OnInit {
    isLoading = false;
    isSaving = false;
    form = new FormGroup({});
    private gsByGtId = new Map<number, number>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: JeTierNachtragenDialogInput,
        private readonly dialogRef: MatDialogRef<
            GruppeTierJeTierNachtragenDialogComponent,
            JeTierNachtragenDialogResult | undefined
        >,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        for (const animal of this.data.animals ?? []) {
            const ctrl = new FormControl<number | null>(animal.value, [Validators.min(0.01)]);
            this.form.addControl(String(animal.gt_id), ctrl);
        }
        this.resolveStandorte();
    }

    get animals() {
        return this.data.animals ?? [];
    }

    ctrlFor(gtId: number): FormControl<number | null> {
        return this.form.get(String(gtId)) as FormControl<number | null>;
    }

    cancel(): void {
        this.dialogRef.close(undefined);
    }

    save(): void {
        if (this.isSaving) {
            return;
        }
        const rows: Array<{ gt_id: number; gewicht: number; gs_id: number }> = [];
        for (const animal of this.animals) {
            const raw = this.ctrlFor(animal.gt_id).value;
            if (raw == null || Number.isNaN(Number(raw)) || !(Number(raw) > 0)) {
                continue;
            }
            const gsId = this.gsByGtId.get(animal.gt_id) || Number(this.data.gsId) || 0;
            if (gsId <= 0) {
                this.messageService.alertMessage(
                    `Kein Standort für ${animal.ohrmarke} gefunden – Gewicht kann nicht gespeichert werden.`,
                );
                return;
            }
            rows.push({ gt_id: animal.gt_id, gewicht: Math.round(Number(raw) * 100) / 100, gs_id: gsId });
        }
        if (!rows.length) {
            this.messageService.alertMessage('Bitte mindestens ein Gewicht > 0 eintragen.');
            return;
        }

        this.isSaving = true;
        from(rows)
            .pipe(
                mergeMap(
                    row => {
                        const body: Record<string, unknown> = {
                            gs_id: row.gs_id,
                            gt_id: row.gt_id,
                        };
                        if (this.data.field === 'abgangsgewicht') {
                            body.abgangsgewicht = row.gewicht;
                        } else {
                            body.einstallgewicht = row.gewicht;
                        }
                        return this.api.post(this.apiBaseUrl + 'gruppe_standort_tier/update.php', body).pipe(
                            map((): number => row.gewicht),
                        );
                    },
                    3,
                ),
                toArray(),
            )
            .subscribe({
                next: (weights: number[]) => {
                    const gesamt = weights.reduce((s, w) => s + w, 0);
                    this.messageService.infoMessage('Einzelgewichte wurden gespeichert.');
                    this.dialogRef.close({
                        saved: true,
                        gesamt: Math.round(gesamt * 100) / 100,
                        anzahlMitWert: weights.length,
                    });
                },
                error: err => {
                    this.messageService.errorMessage('Fehler beim Speichern der Einzelgewichte', err);
                    this.isSaving = false;
                },
            });
    }

    private resolveStandorte(): void {
        const preferred = Number(this.data.gsId) || 0;
        if (preferred > 0) {
            for (const a of this.animals) {
                this.gsByGtId.set(a.gt_id, preferred);
            }
            return;
        }
        const gtIds = this.animals.map(a => a.gt_id).filter(id => id > 0);
        if (!gtIds.length) {
            return;
        }
        this.isLoading = true;
        const params = new HttpParams().set('gt_ids', gtIds.join(','));
        this.api.getAll(this.apiBaseUrl + 'gruppe/read_gt_letzter_standort.php', params).subscribe({
            next: res => {
                for (const rec of res?.records || []) {
                    const gtId = Number(rec?.gt_id);
                    const gsId = Number(rec?.gs_id);
                    if (gtId > 0 && gsId > 0) {
                        this.gsByGtId.set(gtId, gsId);
                    }
                }
                this.isLoading = false;
            },
            error: err => {
                this.messageService.errorMessage('Standorte konnten nicht geladen werden', err);
                this.isLoading = false;
            },
        });
    }
}
